
import { bindDialog, BindDialogReturn, DialogProps, DialogResult, DialogState, useDialogState } from "../../../hooks/use-dialog-state/UseDialogState";
import { Divider, MenuItem, Stack, Typography, useTheme } from "@mui/material";
import { addDays, isValid as isDateValid } from "date-fns";
import React, { useEffect, useMemo, type JSX } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AccessType, DurationTimeframeTypeEnum } from "../../../common/enums/DRCXEnums";
import htmlParse from 'html-react-parser';
import FormCheckbox from "../../forms/form-checkbox/FormCheckbox";
import FormDateTimePicker from "../../forms/form-date-time-picker/FormDateTimePicker";
import FormInput from "../../forms/form-input/FormInput";
import FormSelect from "../../forms/form-select/FormSelect";
import Form from "../../forms/form/Form";
import ResponsiveDialog from "../../responsive-dialog/ResponsiveDialog";
import ObjectGraphUtils from "../ObjectGraphUtils";
import { eLynxObjectTypeEnum } from "../../../common/enums/eLynxObjectType";
import ObjectGraphOptionsUtils, { dateRangeOptions, ObjectGraphOptionsDialogFormValuesType, ObjectGraphOptionsDialogValues } from "./ObjectGraphOptionsUtils";
import FormRadioGroup, { FormRadioGroupRadioButton } from "../../forms/form-radio-group/FormRadioGroup";
import { isMobile, isTablet } from "react-device-detect";
import { DurationOptionEnum, durationRadioButtons } from "../../custom-duration-dialog/CustomDurationUtils";
import FormDateRange from "../../forms/form-date-range/FormDateRange";
import _ from "lodash";
import { IDateRange } from "../../../common/models/IDateRange";
import FormGroupedSelect, { FormGroupedSelectOption } from "../../forms/form-grouped-select/FormGroupedSelect";
import { useRouteContext } from "../../../hooks/use-route-context/UseRouteContext";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import { useObjectGraphConfigurations } from "../../../queries/object-graph-configuration/UseObjectGraphConfigurations";
import GroupPicker from "../../group-picker/GroupPicker";
import { INavigationItem } from "../../../services/navigation-data-service/Models";
import NavigationDataService from "../../../services/navigation-data-service/NavigationDataService";
import { useUserContext } from "../../../contexts/user-context/UserContext";
import { useGroupContents } from "../../../queries/groups/UseGroupContents";
import FormPropertySection from "../../forms/form-property-section/FormPropertySection";
import { IeLynxObject } from "../../../common/models/eLynxObject";

export type ObjectGraphOptionsDialogResult = DialogResult<ObjectGraphOptionsDialogValues>;

export type ObjectGraphOptionsDialogProps = {
    //contextObjectType: number;
} & DialogProps<ObjectGraphOptionsDialogValues, ObjectGraphOptionsDialogResult>;

export function ObjectGraphOptionsDialog({
    open,
    onClose,
    values,
    //contextObjectType
}: ObjectGraphOptionsDialogProps): JSX.Element {
    
    const isPhone = isMobile && !isTablet;
    const theme = useTheme();
    const { t } = useTranslation();
    const routeContext = useRouteContext();
    const [userState] = useUserContext();

    const form = useForm<ObjectGraphOptionsDialogFormValuesType>({
        mode: "onChange",
        defaultValues: useMemo(() => {
            const initialOptions = ObjectGraphUtils.getDefaultDisplayOptions();
            return ObjectGraphOptionsUtils.mapToForm(initialOptions);
    }, [])
    });

    const {
        handleSubmit,
        formState: {
            //errors,
            isDirty,
            isValid,
            //dirtyFields
        },
        getValues,
        setValue,
        watch,
        reset,
        trigger,
        resetField
    } = form;

    const watchAll = watch();
    const [
        durationType,
        multipleStartOffset,
        option,
        configurationID,
        objectID,
        deviceID
     ] = watch([
        'comparisonTimeAndDuration.durationType',
        'comparisonTimeAndDuration.multipleStartOffset',
        'option','configurationID',
        'objectID',
        'deviceID'
    ]);


    const contextObject: IeLynxObject | undefined = values?.contextObjectType === eLynxObjectTypeEnum.Device ? {
        id: deviceID ?? routeContext.deviceID!,
        objectType: eLynxObjectTypeEnum.Device
    } as IeLynxObject : objectID ? {
        id: objectID,
        objectType: eLynxObjectTypeEnum.Group
    } as IeLynxObject : 
        values?.contextObject;
    // const contextObject: IeLynxObject | undefined = values && open && objectID ? {
    //     id: objectType === eLynxObjectTypeEnum.Group ? objectID : deviceID ?? -1,
    //     //objectType: values?.contextObject.objectType ?? eLynxObjectTypeEnum.None
    //     objectType: objectType
    // } : values?.contextObject
    
    const {
        data: graphConfigurations,
    } = useQueryNotifications(useObjectGraphConfigurations)({
        groupID: objectID ?? -1,
        eLynxObject: contextObject,
        enabled: open && values?.showConfigurationSelector && contextObject !== undefined && (objectID ?? -1) !== -1
    });

    const {
        data: availableDevices,
    } = useQueryNotifications(useGroupContents)({
        groupID: objectID ?? -1,
        objectType: eLynxObjectTypeEnum.Device,
        enabled: open && (objectID ?? -1) !== -1 && values?.showDeviceSelector
    });

    const sectionCount = values?.sectionCount;

     const sharedConfigurations = graphConfigurations?.configurations?.filter(o => o.access === AccessType.Shared)
    .map(o => {
        return {
            value: o.id,
            label: o.displayText,
            isDefault: o.id === graphConfigurations?.defaultConfigID
        } as FormGroupedSelectOption;
    }) ?? [];

    const sharedReadOnlyConfigurations = graphConfigurations?.configurations?.filter(o => o.access === AccessType.SharedReadOnly)
    .map(o => {
        return {
            value: o.id,
            label: o.displayText,
            isDefault: o.id === graphConfigurations?.defaultConfigID
        } as FormGroupedSelectOption;
    }) ?? [];

    const privateConfigurations = graphConfigurations?.configurations?.filter(o => o.access === AccessType.Private)
    .map(o => {
        return {
            value: o.id,
            label: o.ownerName
                ? `${o.displayText} (${o.ownerName})`
                : `${o.displayText}`,
            isDefault: o.id === graphConfigurations?.defaultConfigID
        } as FormGroupedSelectOption;
    }) ?? [];


    const durationOptionRadioButtons = useMemo(() => {

        const radioButtons = _.cloneDeep(durationRadioButtons);
    
        radioButtons[0].label = t('customDuration.configured');
        return radioButtons;
    }, [t]);

    
    const _validateMultiStartOffset = (value: string): boolean => {

        return value.toString().trim() !== '' && +value <= 90;
    }

    const radioButtons: FormRadioGroupRadioButton[] = [{
        label: <Stack direction='row' sx={{marginBottom: 2}}>
                <Typography component='div' 
                    sx={{
                        whiteSpace: 'nowrap',
                        display: 'inline-flex',
                        marginTop: multipleStartOffset === undefined || (multipleStartOffset > 90) ? 5 : 3
                    }}>
                        {t("trending.comparisonGraph.beginning")}
                </Typography>

                <FormInput
                    disabled={durationType !== DurationTimeframeTypeEnum.MultipleTimeFrames}
                    name="comparisonTimeAndDuration.multipleStartOffset"
                    
                    numeric={true}
                    sx={{
                        width: theme.spacing(isPhone ? 8 : 16),
                        marginLeft: 1,
                        marginRight: 1,
                        marginTop: multipleStartOffset === undefined || (multipleStartOffset > 90) ? 2 : undefined
                    }}
                    rules={{
                        required: {
                            value: durationType === DurationTimeframeTypeEnum.MultipleTimeFrames,
                            message: "Required"
                        },
                        validate: (value: string) => !_validateMultiStartOffset(value) ?
                        t('trending.messages.maxCompareDurationNEW') : 
                        true
                    }}
                />

                <FormSelect
                        disabled={durationType !== DurationTimeframeTypeEnum.MultipleTimeFrames}
                        name="comparisonTimeAndDuration.multipleStartRangeType"
                        ariaLabel="duration type"
                        sx={{
                            marginRight: 1,
                            marginTop: multipleStartOffset === undefined || (multipleStartOffset > 90) ? 2 : undefined
                        }}>
                    {dateRangeOptions.map((value, index) =>
                        <MenuItem key={index} value={value.id}>
                            {value.name}(s)
                        </MenuItem>)
                    }
                </FormSelect>

                <Typography component='div'
                    sx={{
                        whiteSpace: 'nowrap',
                        display: 'inline-flex',
                        marginTop: multipleStartOffset === undefined ? 5 : 3
                    }}>
                    {t("trending.comparisonGraph.earlier")}
                </Typography>

        </Stack>,
        ariaLabel: t("trending.comparisonGraph.beginning") as string,
        value: DurationTimeframeTypeEnum.MultipleTimeFrames        
    }, {
            label: <Stack direction='row'>
                    <Typography component='div' 
                        sx={{
                            whiteSpace: 'nowrap',
                            display: 'inline-flex',
                            marginTop: 2,
                            //marginBottom: 1
                            marginRight: 1
                        }}>
                            {t("trending.comparisonGraph.startingOn")}
                    </Typography>


                    <FormDateTimePicker
                        ariaLabel="start date"
                        disabled={watchAll?.comparisonTimeAndDuration?.durationType !==
                            DurationTimeframeTypeEnum.SpecificStartOrStop}
                        name="comparisonTimeAndDuration.fixedStartDate"
                        rules={durationType === DurationTimeframeTypeEnum.SpecificStartOrStop ? {
                            required: t('customDuration.validStartDate'),
                            validate: (value: any) => _isInValidateStartDate(value)
                        } : undefined}

                        maxDateTime={new Date()}
                        />



            </Stack>,
        ariaLabel: t("trending.comparisonGraph.startingOn") as string,
        value: DurationTimeframeTypeEnum.SpecificStartOrStop
    }];

    const onCancel = () => {

        onClose && onClose({
            reason: "cancel",
            values: undefined
        });

        reset();
    };

    const _isInValidateStartDate = (value: string): string | undefined => {

        let message: string | undefined = undefined;

        const start = new Date(value);

        if (!isDateValid(start)) {

            message = t('customDuration.validStartDate') as string;

        }

        return message;
    };

    const onDurationTypeChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {

        const value = +event.target.value as DurationTimeframeTypeEnum;

        switch(value) {
            case DurationTimeframeTypeEnum.MultipleTimeFrames:
                resetField("comparisonTimeAndDuration.fixedStartDate");
      
                break;

            case DurationTimeframeTypeEnum.SpecificStartOrStop:

                // when multipleStartOffset is not set to a value (validation error) the actual underlying form value
                // has not changed, so in order to get it to rerender when resetting the field, change it and then reset 
                // the field
                setValue('comparisonTimeAndDuration.multipleStartOffset', 0);

                setTimeout(() => {            
                    resetField("comparisonTimeAndDuration.multipleStartOffset", {
                    });
                },1);

                resetField("comparisonTimeAndDuration.multipleStartRangeType");
                
                trigger('comparisonTimeAndDuration.multipleStartOffset');                

                break;
        }
    };

    const onOk = (formValues: ObjectGraphOptionsDialogFormValuesType) => {

        const model = ObjectGraphOptionsUtils.mapToModel(formValues);


        model.configurationName = graphConfigurations?.configurations?.find(o => o.id === model.configurationID)?.displayText ?? '';

        const dialogValues: ObjectGraphOptionsDialogValues = {
            groupID: model.groupID,
            contextObject: model.contextObject,

            contextObjectType: contextObject?.objectType ?? eLynxObjectTypeEnum.None,

            configurationID: model.configurationID,
            configurationName: model.configurationName,
            displayOptions: model.displayOptions,
            durationOptions:  model.durationOptions
        };

        if (dialogValues.durationOptions) {
            switch (dialogValues.durationOptions.option) {
                case DurationOptionEnum.Default:
                    break;

                case DurationOptionEnum.OneDay:
                case DurationOptionEnum.ThreeDays:
                case DurationOptionEnum.SevenDays:
                case DurationOptionEnum.ThirtyDays:
                    const today = new Date();
                    dialogValues.durationOptions.duration.startDate = addDays(today, -(+dialogValues.durationOptions.option));
                    dialogValues.durationOptions.duration.endDate = today;

                    break;

                case DurationOptionEnum.Custom:
                    dialogValues.durationOptions.duration.startDate = formValues.duration!.startDate;
                    dialogValues.durationOptions.duration.endDate = formValues.duration!.endDate;
                    break;
            }
        }

        onClose && onClose({
            reason: "ok",
            values: dialogValues
        });

        reset();
    };

    function clearFields() {

        if (values) {

            setValue('option', DurationOptionEnum.Default, {shouldDirty: true});
            setValue('duration', {
                startDate: addDays(new Date(), -7),
                endDate: new Date()
            } as IDateRange, {shouldDirty: true});


            const initialOptions = ObjectGraphUtils.getDefaultDisplayOptions();
            setValue('displayAlarms', initialOptions.displayOptions!.displayAlarms!, {shouldDirty: true});
            setValue('displayDataQualityEvents', initialOptions.displayOptions!.displayDataQualityEvents!, {shouldDirty: true});
            setValue('displayDiagnosticEvents', initialOptions.displayOptions!.displayDiagnosticEvents!, {shouldDirty: true});
            setValue('displayManualEvents', initialOptions.displayOptions!.displayManualEvents!, {shouldDirty: true});
            setValue('displayLegend', initialOptions.displayOptions!.displayLegend!, {shouldDirty: true});
            setValue('displayMemos', initialOptions.displayOptions!.displayMemos!, {shouldDirty: true});
            setValue('displaySectionsStacked', initialOptions.displayOptions!.displaySectionsStacked!, {shouldDirty: true});
            setValue('displayTabularData', initialOptions.displayOptions!.displayTabularData!, {shouldDirty: true})
            setValue('displaySettingsChanges', initialOptions.displayOptions!.displaySettingsChanges!, {shouldDirty: true});
            setValue('displaySimulationDataQualityEvents', initialOptions.displayOptions!.displaySimulationDataQualityEvents!, {shouldDirty: true});
            setValue('displaySimulationDiagnosticEvents', initialOptions.displayOptions!.displaySimulationDiagnosticEvents!, {shouldDirty: true});

            setValue('hasComparisonTimeframe', initialOptions.displayOptions!.hasComparisonTimeframe!, {shouldDirty: true});
            setValue('comparisonTimeAndDuration.durationType', initialOptions.displayOptions!.comparisonTimeAndDuration?.durationType!, {shouldDirty: true});
            setValue('comparisonTimeAndDuration.multipleStartOffset', initialOptions.displayOptions!.comparisonTimeAndDuration?.multipleStartOffset!, {shouldDirty: true});
            setValue('comparisonTimeAndDuration.multipleStartRangeType', initialOptions.displayOptions!.comparisonTimeAndDuration?.multipleStartRangeType!, {shouldDirty: true});
            setValue('comparisonTimeAndDuration.fixedStartDate', initialOptions.displayOptions!.comparisonTimeAndDuration?.fixedStartDate!, {shouldDirty: true});

            setValue('objectID', null);
            setValue('configurationID', null);
        }
    }


    const onShowComparisonChanged = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean): void => {

        resetField("comparisonTimeAndDuration.durationType");
        resetField("comparisonTimeAndDuration.multipleStartOffset");
        resetField("comparisonTimeAndDuration.multipleStartRangeType");
        resetField("comparisonTimeAndDuration.fixedStartDate");
    };

    function onDurationOptionChange() {
        const newOption = +getValues('option') as DurationOptionEnum;
        setValue('option', newOption);
    }

    function onSetGroup(item: INavigationItem) {

        setValue('objectID', item.objectID, {shouldDirty: true});
        setValue('objectName', item.title);
        setValue('configurationID', null, {shouldDirty: true});
    }

    function onDeviceChanged() {
        const value = getValues('deviceID');
        const device = availableDevices?.find(o => o.objectID === value);
        setValue('objectName', device?.name ?? '');
        setValue('configurationID', null, {shouldDirty: true});            
    }


    useEffect(() => {

        const resetForm = async () => {

            if (values) {
                const data = _.cloneDeep(values);

                if (data && data.contextObject.objectType === eLynxObjectTypeEnum.Group) {
                    const group = await NavigationDataService.getNavigationGroup(userState.customer!.id, data.contextObject.id, {});
                    data.contextObject.displayText = group?.title;
                } 

                reset(ObjectGraphOptionsUtils.mapToForm(data));                
            }
        }

        if (values)
        {
            //reset(ObjectGraphOptionsUtils.mapToForm(values));
            resetForm();
        }
    }, [
        reset, 
        values,
        userState.customer
    ]);

    useEffect(() => {
        if (values?.durationOptions && option !== DurationOptionEnum.Custom) {

            const formValues = getValues();

            const endDate = formValues.duration?.endDate ?? new Date();

            if (!formValues.duration?.endDate) {
                setValue('duration.endDate', endDate);
            }

            if (!formValues.duration?.startDate) {
                setValue('duration.startDate', addDays(endDate, -7));
            }
        }

    }, [
        values,
        option, 
        getValues,
        setValue
    ]);

    
    return (
        <>
            <ResponsiveDialog
                title={t("trending.overrideOptions.title")}
                enableOkay={isDirty && isValid && (values?.showConfigurationSelector ? configurationID !== null : true)}
                okayLabel={t("buttons.okay")}
                open={open}
                onClose={
                    (reason: string) => {
                        reason === "ok"
                            ? handleSubmit((data) => onOk(data))()
                            : onCancel();
                    }
                }
                
                enableAdditionalAction={values?.showReset}
                additionalActionLabel={t("buttons.reset")}
                onAdditionalAction={() => clearFields()}
                paperProps={{
                        sx: {
                            display: 'flex',
                        }}
                }
                contentsx={{
                    overflow: 'hidden',
                    display: 'flex',
                }}
    
                >

                <Form form={form} sx={{overflowY: 'auto'}}>

                    {
                        values?.durationOptions &&
                        <Stack spacing={2} sx={{overflow: 'hidden', flex: '1 0 auto', paddingRight: theme.spacing(1)}}>

                            {values?.showGroupSelector &&
                            
                            <GroupPicker
                                    groupID={objectID ?? undefined}
                                    onSelected={(item) => onSetGroup(item)}
                                    vertical='top'
                                    rootID={routeContext.groupID!}
                                    textFieldLabel={true}
                                    label='Choose a group'
                                />            
                            }

                            {values?.showDeviceSelector &&


                            <FormPropertySection label={undefined}>

                                <FormSelect
                                    name="deviceID"
                                    label='Choose a device'
                                    sx={{ 
                                        width: 1,
                                        marginTop: 1,
                                    }}
                                    rules={{
                                        required: "Required"
                                    }}
                                    onChange={() => onDeviceChanged()}
                                >

                                    <MenuItem value={-1} disabled={true}>
                                    </MenuItem>
                                    {
                                    availableDevices?.map((o, index) => 
                                        <MenuItem key={index} value={o.objectID}>
                                            {o.name}
                                        </MenuItem>
                                    )
                                    }

                                </FormSelect>

                            </FormPropertySection>


                            }



                            {
                            values?.showConfigurationSelector && graphConfigurations &&
                            <FormGroupedSelect
                                name='configurationID'
                                label={t("trending.configuration.list.placeholder")}
                                //placeholder={t("configurationList.defaultCamelCase")}
                                optionGroups={[{
                                    heading: t("trending.configuration.list.shared"),
                                    options: sharedConfigurations
                                }, {
                                    heading: t("trending.configuration.list.sharedReadOnly"),
                                    options: sharedReadOnlyConfigurations
                                }, {
                                    heading: t("trending.configuration.list.private"),
                                    options: privateConfigurations
                                }]}
                                //onChange={(configurationID: number) => onConfigurationSelected && onConfigurationSelected(configurationID)}
                            />
                            }

                        <FormRadioGroup label='Duration'
                            name='option'
                            radioButtons={durationOptionRadioButtons} 
                            onChange={onDurationOptionChange}
                        />
                        <FormDateRange
                            name='duration'
                            disabled={+option !== DurationOptionEnum.Custom ? true : undefined}
                            spacing={{ xs: 2, sm: 4 }}
                        />
    
                        <Divider />
    
                    </Stack>
    
                    }

                    <Stack direction={{
                        xs: "column", md: "row"
                    }}
                        spacing={2}
                        sx={{
                            whiteSpace: "nowrap"
                        }}>
                        <Stack>

                            <FormCheckbox
                                label={t("trending.overrideOptions.optionSelections.showMemos.name")}
                                name="displayMemos"
                            />
                            <FormCheckbox
                                label={t("trending.overrideOptions.optionSelections.showAlarms.name")}
                                name="displayAlarms"
                            />
                            {values?.contextObject.objectType === eLynxObjectTypeEnum.Group &&

                                <FormCheckbox
                                    disabled={((sectionCount ?? 0) < 2) ||
                                        watchAll?.hasComparisonTimeframe}

                                    label={t("trending.overrideOptions.optionSelections.showSectionsStacked.name")}
                                    name="displaySectionsStacked"
                                    tooltip={((sectionCount ?? 0) < 2)
                                        ? htmlParse(t("trending.configuration.steps.graphOptions.tooltips.warnNotAvailWithSingleSection")) as JSX.Element
                                        : watchAll?.hasComparisonTimeframe
                                            ? htmlParse(t("trending.configuration.steps.graphOptions.tooltips.warnNotAvailWithCompare")) as JSX.Element
                                            : undefined}
                                />
                            }

                            {values?.contextObject && values.contextObject.objectType !== eLynxObjectTypeEnum.Group &&
                                <>
                                    <FormCheckbox
                                        label={t("trending.overrideOptions.optionSelections.showDataQuality.name")}
                                        name="displayDataQualityEvents"
                                    />
                                    <FormCheckbox
                                        label={t("trending.overrideOptions.optionSelections.showDiagnostics.name")}
                                        name="displayDiagnosticEvents"
                                    />

                                    <FormCheckbox
                                        label={t("trending.overrideOptions.optionSelections.showManualEvents.name")}
                                        name="displayManualEvents"
                                    />

                                    {!values?.hideShowStacked &&
                                        <FormCheckbox
                                            disabled={((sectionCount ?? 0) < 2) ||
                                                watchAll?.hasComparisonTimeframe}

                                            label={t("trending.overrideOptions.optionSelections.showSectionsStacked.name")}
                                            name="displaySectionsStacked"
                                            tooltip={((sectionCount ?? 0) < 2)
                                                ? htmlParse(t("trending.configuration.steps.graphOptions.tooltips.warnNotAvailWithSingleSection")) as JSX.Element
                                                : watchAll?.hasComparisonTimeframe
                                                    ? htmlParse(t("trending.configuration.steps.graphOptions.tooltips.warnNotAvailWithCompare")) as JSX.Element
                                                    : undefined}
                                        />}
                                </>
                            }
                            <FormCheckbox
                                label={t("trending.overrideOptions.optionSelections.showSettingsChanges.name")}
                                name="displaySettingsChanges"
                            />
                            <FormCheckbox
                                label={t("trending.overrideOptions.optionSelections.showLegend.name")}
                                name="displayLegend"
                            />
                            <FormCheckbox
                                label={t("trending.overrideOptions.optionSelections.showTabularData.name")}
                                name="displayTabularData"
                            />
                        </Stack>

                        {values?.contextObject && values.contextObject.objectType !== eLynxObjectTypeEnum.Group && !values?.hideShowAdditional &&
                            <Stack
                                style={{
                                    marginTop: "0"
                                }}>
                                <FormCheckbox
                                    disabled={watchAll?.displaySectionsStacked}
                                    label={t("trending.overrideOptions.optionSelections.showComparison.name")}
                                    name="hasComparisonTimeframe"
                                    onChange={onShowComparisonChanged}
                                    tooltip={watchAll?.displaySectionsStacked ?
                                        htmlParse(t("trending.configuration.steps.graphOptions.tooltips.warnNotAvailWithStacked")) as JSX.Element
                                        : undefined}
                                />

                                {watchAll?.hasComparisonTimeframe &&




                                <FormRadioGroup label=''
                                    name='comparisonTimeAndDuration.durationType'
                                    radioButtons={radioButtons} 
                                    onChange={onDurationTypeChanged}
                                />

                                }
                            </Stack>
                        }
                    </Stack>
                </Form>
            </ResponsiveDialog>
        </>
    );
}

export type ObjectGraphOptionsDialogState = DialogState<ObjectGraphOptionsDialogValues, ObjectGraphOptionsDialogResult>;
export type BindObjectGraphOptionsDialogReturn = BindDialogReturn<ObjectGraphOptionsDialogValues, ObjectGraphOptionsDialogResult>;

export function useObjectGraphOptionsDialogState(): ObjectGraphOptionsDialogState {
    return useDialogState();
}

export function bindObjectGraphOptionsDialog(props: ObjectGraphOptionsDialogState): BindObjectGraphOptionsDialogReturn {
    return bindDialog(props);
}


